import { ErrorHandler, Injectable } from '@angular/core';
import { sortBy } from 'lodash';
import { Observable } from 'rxjs';

import { map, tap } from 'rxjs/operators';
import { FilterDimension } from 'app/hub-components/models/filter-dimension.model';
import { Dataset } from '../../models/analytics-api/dataset.model';
import { Query } from '../../models/analytics-api/query.model';
import { QueryResult } from '../../models/analytics-api/query_result.model';
import { Dimension, Schema } from '../../models/analytics-api/schema.model';
import { SearchResult } from '../../models/analytics-api/search_result.model';
import { SlicesResult } from '../../models/analytics-api/slices_result.model';
import { ApiService } from '../api/api.service';
import { RequestOptionsArgs } from '../backend/backend.service';
import { MessageService } from '../message/message.service';
import { SessionService } from '../session/session.service';

@Injectable()
export class AnalyticsApiService {
  private reportedMissingRates = new Set<string>();

  constructor(
    private api: ApiService,
    private sessionService: SessionService,
    private messageService: MessageService,
    private errorHandler: ErrorHandler,
  ) {}

  loadDatasets(options: RequestOptionsArgs = { cache: true }) {
    // Temporal httpHeaders
    options.excludeHeaders = true;
    if (this.sessionService.publicId) {
      options.params ||= {};
      options.params['public_id'] = this.sessionService.publicId;
    }
    return this.api.load('/analytics', options)
      .pipe(
        map(rawDatasets => {
          const datasets = (rawDatasets || [])
            .map(dataset => new Dataset(dataset['name'], dataset['type'], dataset['meta']));
          return sortBy(datasets, (dataset: Dataset) => dataset.displayName);
        }),
      );
  }

  loadSchema(datasetName: string, options: RequestOptionsArgs = { cache: true }) {
    if (this.sessionService.publicId) {
      options.params ||= {};
      options.params['public_id'] = this.sessionService.publicId;
    }
    return this.api.load(`/analytics/${datasetName}/schema`, options)
      .pipe(
        map(rawSchema => new Schema(rawSchema)),
      );
  }

  runQuery(query: Query, options: RequestOptionsArgs = {}): Observable<QueryResult> {
    return this.api.create(`/analytics/${query.dataset}/series`, query.getQueryBody(), { ...options, operation: 'AnalyticsApiService#runQuery' })
      .pipe(
        map(result => new QueryResult(query, result)),
        tap(result => this.reportMissingRates(result)),
      );
  }

  querySlices(query: Query, options: RequestOptionsArgs = {}, filterDimensions?: FilterDimension[]): Observable<SlicesResult> {
    return this.api.create(`/analytics/${query.dataset}/slices`, query.getSlicesBody(filterDimensions), { ...options, operation: 'AnalyticsApiService#querySlices' })
      .pipe(
        map(result => new SlicesResult(query, result)),
      );
  }

  search(query: Query, dimension: Dimension, term: string, options: RequestOptionsArgs = { cache: true, skipLoader: true }, limit = 20, data: string[] = undefined) {
    return this.api.create(`/analytics/${query.dataset}/search`, query.getSearchBody(dimension, term, limit, data), { ...options, operation: 'AnalyticsApiService#search' })
      .pipe(
        map(result => new SearchResult(query, result)),
      );
  }

  private reportMissingRates(result: QueryResult): void {
    const warnings = result.warnings.filter(warning => warning.type === 'missing_currency_rate');
    if (!warnings.length) {
      return;
    }
    warnings.forEach(warning => this.reportMissingRate(result.query.dataset, warning.metric));
    const metrics = warnings.map(warning => result.schema?.fetchMetric(warning.metric)?.displayName ?? warning.metric);
    this.messageService.warning(`Some converted values are incomplete because no exchange rate was found for the following metric(s): ${metrics.join(', ')}.`);
  }

  private reportMissingRate(dataset: string, metric: string): void {
    const key = `${dataset}:${metric}`;
    if (this.reportedMissingRates.has(key)) {
      return;
    }
    this.reportedMissingRates.add(key);
    this.errorHandler.handleError(new Error(`Missing currency rates: ${dataset} ${metric}`));
  }
}
